// DutyLog Enhancement Engine - TypeScript port of proven JavaScript implementation
// Maintains all research-validated enhancement patterns and logic

import {
  EnhancementResult,
  EnhancementChange,
  TagRationale,
  ChangeInfo,
  AbbreviationDictionary,
  EnhancementRules,
  CrosslinkEnhancementResult,
  EnhancementMetadata,
  CrosslinkMetadata
} from './types';
import { abbreviationDictionary } from './enhancementLookupTables';
import { enhancementRules } from './enhancementRules';

export class DutyLogEnhancer {
  private abbreviations: AbbreviationDictionary;
  private rules: EnhancementRules;

  constructor() {
    this.abbreviations = abbreviationDictionary as AbbreviationDictionary;
    this.rules = enhancementRules as EnhancementRules;
  }

  /**
   * Main enhancement function - processes a duty log entry
   * @param logBody - Raw duty log text
   * @param logYear - Year of the duty log (e.g., 2024, 2025)
   * @param logClass - Optional log classification (for direct regional tagging)
   * @returns Enhanced log with improvements and tags
   */
  enhanceLogEntry(logBody: string, logYear: number = 2025, logClass?: string): EnhancementResult {
    const changes: EnhancementChange[] = [];
    const tags = new Set<string>();
    const tagRationales: TagRationale[] = [];

    let result = logBody.trim();    // PASS 0: Clean up single square brackets (typos)
    result = this.cleanupSingleBrackets(result, changes);

    // Enhancement pipeline - order matters for context detection and collision prevention
    // All methods use square brackets and skip content already in brackets for modification
    // BUT still read bracketed content for context analysis
    result = this.fixCommonTypos(result, changes);
    result = this.expandCommonAbbreviations(result, changes);
    result = this.standardizeTimes(result, changes);
    result = this.enhancePricingDetection(result, changes);
    result = this.enhancePrecipitationMeasurements(result, changes);
    result = this.expandOfficerRoles(result, changes);
    result = this.expandSystemCodes(result, changes);
    result = this.expandOrganizationCodes(result, changes);
    result = this.expandEquipmentCodes(result, changes);
    result = this.enhanceFireCodes(result, changes, logYear);
    result = this.enhanceTDOCallFireNumbers(result, changes, logYear); // NEW: Priority 1 from handoff
    result = this.enhanceBDCodes(result, changes); // Before region aliases to prevent collisions
    result = this.enhanceCrewIdentifiers(result, changes);
    result = this.enhanceGeographicReferences(result, changes); // BEFORE aircraft to prevent TLA heuristic conflicts
    result = this.enhanceAircraftReferences(result, changes);
    result = this.enhanceFireBases(result, changes);
    result = this.expandStatusCodes(result, changes);
    result = this.enhanceAviationTerminology(result, changes);
    result = this.expandWildfireTerminology(result, changes);
    result = this.enhanceValuesAtRisk(result, changes);
    result = this.enhanceCommunityReferences(result, changes);
    result = this.fixPunctuation(result, changes);
    result = this.improveIncompleteReferences(result, changes);

    // Add regional tags from logClass if provided (direct classification)
    if (logClass) {
      this.extractRegionalTagsFromLogClass(logClass, tags, tagRationales);
    }

    // Extract tags from ORIGINAL text, not enhanced text, to avoid false positives
    this.extractTags(logBody, tags, tagRationales, logClass, logBody);

    return {
      enhanced_text: result,
      changes: changes,
      tags: Array.from(tags),
      tag_rationales: tagRationales,
      original_text: logBody
    };
  }

  /**
   * Auto-generate tag reason based on tag name and content
   */
  private generateTagReason(tag: string, text: string): string {
    // Simple auto-generation based on tag patterns
    if (tag.startsWith('FIRE_')) return `Detected fire code: ${tag.replace('FIRE_', '')}`;
    if (tag.startsWith('REGION_')) return `Regional classification: ${tag.replace('REGION_', '').replace(/_/g, ' ')}`;
    if (tag === 'LOGISTICS') return 'Detected logistics operations terminology';
    if (tag === 'OPERATIONS') return 'Detected operational terminology';
    if (tag === 'PERSONNEL') return 'Detected personnel management terminology';
    if (tag === 'TRAINING') return 'Detected training or exercise terminology';
    if (tag === 'TDO_CALL') return 'Detected TDO call structure';
    if (tag === 'PRIORITY_URGENT') return 'Detected urgent priority terminology';
    if (tag === 'FUEL_LOGISTICS') return 'Detected fuel logistics terminology';
    if (tag === 'COMMUNICATIONS') return 'Detected communication terminology';
    if (tag === 'REGIONAL_LOG') return 'Classified as regional log';
    return `Detected ${tag.toLowerCase().replace(/_/g, ' ')} indicators`;
  }

  /**
   * Clean up single square brackets (typos) - 75% are typos
   */
  private cleanupSingleBrackets(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Remove orphaned opening brackets
    const openPattern = /\[[^\]]*$/g;
    if (openPattern.test(result)) {
      result = result.replace(openPattern, '');
      changes.push({
        type: 'bracket_cleanup',
        description: 'Removed orphaned opening square bracket'
      });
    }

    // Remove orphaned closing brackets  
    const closePattern = /^[^\[]*\]/g;
    if (closePattern.test(result)) {
      result = result.replace(closePattern, '');
      changes.push({
        type: 'bracket_cleanup',
        description: 'Removed orphaned closing square bracket'
      });
    }

    return result;
  }

  /**
   * Check if a text position is inside existing square brackets
   * This is used to SKIP modification of bracketed content
   * BUT bracketed content is still READ for context analysis
   */
  private isInsideBrackets(text: string, startPos: number, endPos: number): boolean {
    // Find all bracket pairs before this position
    let openCount = 0;
    for (let i = 0; i < startPos; i++) {
      if (text[i] === '[') openCount++;
      if (text[i] === ']') openCount--;
    }
    return openCount > 0;
  }

  /**
   * Check if a code has already been enhanced anywhere in the text
   * Prevents duplicate enhancements like "ZF [Yellowknife] [Yellowknife]" and "drums [Aviation Fuel Storage Drums] [Aviation Fuel Storage Drums]"
   */
  private isAlreadyEnhanced(text: string, code: string): boolean {
    // Check for direct enhancement: "code [enhancement]"
    const directPattern = new RegExp(`\\b${code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\[`, 'i');
    if (directPattern.test(text)) return true;

    // Check for already enhanced text: "code [enhancement] [enhancement]" (multiple brackets)
    const multiplePattern = new RegExp(`\\b${code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\[[^\\]]+\\]\\s*\\[`, 'i');
    return multiplePattern.test(text);
  }

  /**
   * Safe regex replace that skips content inside brackets for MODIFICATION
   * but bracketed content is still available for context reading
   */
  private safeReplace(
    text: string,
    pattern: RegExp,
    replacement: string | ((match: string, ...args: any[]) => string),
    changes: EnhancementChange[],
    changeInfo: ChangeInfo
  ): string {
    let result = text;
    const matches = Array.from(text.matchAll(new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g')));

    for (const match of matches.reverse()) { // Reverse to maintain positions
      const start = match.index!;
      const end = start + match[0].length;

      // Debug for demob specifically
      if (match[0].toLowerCase() === 'demob') {
        console.log('DEBUG SAFEREPLACE: Processing demob match...');
        console.log('DEBUG SAFEREPLACE: match:', match[0]);
        console.log('DEBUG SAFEREPLACE: start:', start, 'end:', end);
        console.log('DEBUG SAFEREPLACE: isInsideBrackets result:', this.isInsideBrackets(text, start, end));
        console.log('DEBUG SAFEREPLACE: text context:', text.substring(Math.max(0, start - 10), Math.min(text.length, end + 10)));
      }

      if (!this.isInsideBrackets(text, start, end)) {
        const originalText = match[0];
        const newText = typeof replacement === 'string' ?
          replacement : replacement(match[0], ...match.slice(1));

        // Debug for demob specifically
        if (originalText.toLowerCase() === 'demob') {
          console.log('DEBUG SAFEREPLACE: demob not in brackets, calling replacement function...');
          console.log('DEBUG SAFEREPLACE: originalText:', originalText);
          console.log('DEBUG SAFEREPLACE: newText:', newText);
          console.log('DEBUG SAFEREPLACE: texts are different?', originalText !== newText);
        }

        if (originalText !== newText) {
          result = result.substring(0, start) + newText + result.substring(end);

          const description = typeof changeInfo.description === 'function' ?
            changeInfo.description(originalText, newText) : changeInfo.description;

          changes.push({
            type: changeInfo.type,
            description: description,
            original: originalText,
            enhanced: newText
          });
        }
      } else {
        // Debug for demob if it's being blocked by brackets
        if (match[0].toLowerCase() === 'demob') {
          console.log('DEBUG SAFEREPLACE: demob blocked by isInsideBrackets check!');
        }
      }
    }

    return result;
  }

  /**
   * Fix common typos and misspellings
   */
  private fixCommonTypos(text: string, changes: EnhancementChange[]): string {
    console.log('[### BREADCRUMB] fixCommonTypos logic executed');
    let result = text;

    const commonTypos = this.rules.common_typos || {};
    console.log('[### BREADCRUMB] fixCommonTypos', commonTypos);
    for (const [typo, correction] of Object.entries(commonTypos)) {
      const pattern = new RegExp('\\b' + typo.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&') + '\\b', 'gi');
      if (pattern.test(result)) {
        console.log(`[### BREADCRUMB] Typo detected: '${typo}' will be corrected to '${correction}'`);
      }
      result = this.safeReplace(result, pattern, correction, changes, {
        type: 'typo_correction',
        description: (orig, repl) => `Fixed typo: ${orig} → ${repl}`
      });
    }

    return result;
  }

  /**
   * Expand common abbreviations
   */
  private expandCommonAbbreviations(text: string, changes: EnhancementChange[]): string {
    let result = text;
    const abbreviations = this.abbreviations.common_abbreviations || {};

    for (const [abbrev, expansion] of Object.entries(abbreviations)) {
      const pattern = new RegExp('\\b' + abbrev.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${expansion}]`;
      }, changes, {
        type: 'common_abbreviation_expansion',
        description: (orig, repl) => `Expanded abbreviation: ${orig} → ${expansion}`
      });
    }

    return result;
  }

  /**
   * Standardize time formats to HH:MM hours
   */
  private standardizeTimes(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Time decompression: @10-19:00 -> From 10:00 to 19:00
    const timeDecompressionPattern = /@(\d{1,2})-(\d{1,2}):(\d{2})/g;
    result = this.safeReplace(result, timeDecompressionPattern, (match, startHour, endHour, minutes) => {
      return `From ${startHour}:${minutes} to ${endHour}:${minutes}`;
    }, changes, {
      type: 'time_decompression',
      description: (orig, repl) => `Expanded time range: ${orig} → ${repl}`
    });

    // Handle @HHMM time pattern (common in aviation/duty logs)
    const atTimePattern = /@([01]?\d|2[0-3])([0-5]\d)\b/g;
    result = this.safeReplace(result, atTimePattern, (match, hours, minutes) => {
      return `@${hours}:${minutes}`;
    }, changes, {
      type: 'at_time_standardization',
      description: (orig, repl) => `Standardized @ time format: ${orig} → ${repl}`
    });

    // FIXED: 24-hour time without colon - EXCLUDE coordinates, phone numbers, years, and measurements
    // More restrictive pattern: must be standalone time, not part of coordinates, phone numbers, years, or measurements
    const timePattern = /\b([01]?\d|2[0-3])([0-5]\d)\b(?!\s*(?:hours?|hrs?|minutes?|mins?|AM|PM|am|pm|\.|\-\d|\d))/g;
    result = this.safeReplace(result, timePattern, (match, hours, minutes) => {
      // Additional safety check: avoid phone numbers, coordinates, years, and measurements
      const contextBefore = text.substring(Math.max(0, text.indexOf(match) - 15), text.indexOf(match));
      const contextAfter = text.substring(text.indexOf(match) + match.length, text.indexOf(match) + match.length + 15);

      // Skip if this looks like part of phone number, coordinate, or year
      if (/\d\-$/.test(contextBefore) || /^[\-\.]/.test(contextAfter)) {
        return match;
      }

      // Skip if this is a 4-digit year (starts with 19xx or 20xx)
      if (match.match(/^(19|20)\d{2}$/)) {
        return match;
      }

      // Skip if this appears to be a measurement or cost - check exclusion contexts
      const exclusionContexts = this.rules.bd_code_disambiguation?.time_exclusion_contexts || [];
      const fullContext = contextBefore + match + contextAfter;

      for (const context of exclusionContexts) {
        const contextRegex = new RegExp(`\\b\\d+\\s*(${context})|\\b(${context})\\s*\\d+|\\b\\d+\\s+(${context})|\\$(\\d+)|\\d+\\s*per\\s+(day|hour)`, 'i');
        if (contextRegex.test(fullContext)) {
          return match; // Don't convert measurements/costs to time
        }
      }

      // Additional specific checks for common measurement patterns
      if (/\b\d+\s*(meters?|metres?|km|per\s+day|dollars?|\$|quote|hectares?|ha)\b/i.test(fullContext)) {
        return match; // Keep as measurement, not time
      }

      return `${hours}:${minutes}`;
    }, changes, {
      type: 'time_standardization',
      description: (orig, repl) => `Standardized time format: ${orig} → ${repl}`
    });

    return result;
  }

  /**
   * Enhance pricing detection - add currency symbols and tag financial content
   */
  private enhancePricingDetection(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Check for existing pricing patterns from rules
    if (this.rules.pricing_detection?.patterns) {
      for (const patternRule of this.rules.pricing_detection.patterns) {
        const pattern = new RegExp(patternRule.pattern, 'gi');
        result = this.safeReplace(result, pattern, (match, amount, timeUnit) => {
          return patternRule.replacement.replace(/\$1/g, amount).replace(/\$2/g, timeUnit);
        }, changes, {
          type: 'pricing_enhancement',
          description: (orig, repl) => `Enhanced pricing: ${orig} → ${repl}`
        });
      }
    }

    return result;
  }

  /**
   * Enhance precipitation measurements - standardize mils/ml references
   */
  private enhancePrecipitationMeasurements(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Pattern for precipitation measurements: number + mil/mils/ml
    const precipPattern = /\b(\d+(?:\.\d+)?)\s*(mil|mils|ml)\b/gi;

    result = this.safeReplace(result, precipPattern, (match, number, unit) => {
      const standardizedUnit = unit.toLowerCase() === 'ml' ? 'ml' : 'mm';
      const unitDescription = standardizedUnit === 'ml' ? 'milliliters' : 'millimeters';
      return `${number}${standardizedUnit} [${number} ${unitDescription} of precipitation]`;
    }, changes, {
      type: 'precipitation_measurement_enhancement',
      description: (orig, repl) => `Enhanced precipitation measurement: ${orig} → precipitation context`
    });

    return result;
  }

  /**
   * Expand officer role abbreviations
   */
  private expandOfficerRoles(text: string, changes: EnhancementChange[]): string {
    let result = text;
    const roles = this.abbreviations.personnel_roles || {};

    for (const [code, fullRole] of Object.entries(roles)) {
      const pattern = new RegExp('\\b' + code + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${fullRole}]`;
      }, changes, {
        type: 'officer_role_expansion',
        description: (orig, repl) => `Expanded officer role: ${orig} → ${fullRole}`
      });
    }

    return result;
  }

  /**
   * Expand system codes (SPARCS, VIIRS, IntelliFire)
   */
  private expandSystemCodes(text: string, changes: EnhancementChange[]): string {
    let result = text;
    const systems = this.abbreviations.systems || {};

    for (const [code, fullName] of Object.entries(systems)) {
      const pattern = new RegExp('\\b' + code + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${fullName}]`;
      }, changes, {
        type: 'system_expansion',
        description: (orig, repl) => `Expanded system: ${orig} → ${fullName}`
      });
    }

    return result;
  }

  /**
   * Expand organization codes
   */
  private expandOrganizationCodes(text: string, changes: EnhancementChange[]): string {
    let result = text;
    const organizations = this.abbreviations.organizations || {};

    for (const [code, fullName] of Object.entries(organizations)) {
      const pattern = new RegExp('\\b' + code + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${fullName}]`;
      }, changes, {
        type: 'organization_expansion',
        description: (orig, repl) => `Expanded organization: ${orig} → ${fullName}`
      });
    }

    return result;
  }

  /**
   * Expand equipment codes and operational status indicators
   */
  private expandEquipmentCodes(text: string, changes: EnhancementChange[]): string {
    let result = text;
    const equipment = this.abbreviations.equipment_codes || {};

    for (const [code, fullName] of Object.entries(equipment)) {
      // Skip if already enhanced
      if (this.isAlreadyEnhanced(result, code)) continue;

      const pattern = new RegExp('\\b' + code + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${fullName}]`;
      }, changes, {
        type: 'equipment_expansion',
        description: (orig, repl) => `Expanded equipment code: ${orig} → ${fullName}`
      });
    }

    return result;
  }

  /**
   * Enhanced fire code standardization with LOCAL context-aware year inference
   */
  private enhanceFireCodes(text: string, changes: EnhancementChange[], logYear: number): string {
    let result = text;

    // FIRST: Handle malformed fire number patterns (with underscores, spaces, etc.)
    const malformedFirePattern = /\b(EV|VQ|ZF|FS|WB|SS)[_\s]+(\d{1,3})\b(?!-\d{2})/g;
    result = this.safeReplace(result, malformedFirePattern, (match, regionCode, fireNumber) => {
      const standardizedNumber = fireNumber.padStart(3, '0');
      const yearSuffix = String(logYear).slice(-2);
      const corrected = `${regionCode}${standardizedNumber}-${yearSuffix}`;
      return corrected;
    }, changes, {
      type: 'fire_code_correction',
      description: (orig, repl) => `Corrected malformed fire number: ${orig} → ${repl}`
    });

    // Main fire code pattern - SS fires vs SM region: SM is region code, SS is fire code prefix
    const firePattern = /\b(EV|VQ|ZF|FS|WB|SS)(\d{1,4})(-\d{2})?\b/g;

    result = this.safeReplace(result, firePattern, (match, regionCode, fireNumber, existingYear) => {
      // Don't modify if already has year
      if (existingYear) {
        return match;
      }

      // ABSOLUTE RULE: Leading zeros = definitively a fire code
      const hasLeadingZero = fireNumber.length > 1 && fireNumber.charAt(0) === '0';
      if (hasLeadingZero) {
        // Definitely a fire code - crews never have leading zeros
        const inferredYear = this.inferFireYearLocal(text, match, logYear);
        const standardizedNumber = fireNumber.padStart(3, '0');
        const yearSuffix = String(inferredYear).slice(-2);
        const enhanced = `${regionCode}${standardizedNumber}-${yearSuffix}`;

        changes.push({
          type: 'fire_code',
          description: 'Leading zero detected - definitive fire code',
          original: match,
          enhanced: enhanced
        });

        return enhanced;
      }

      // CONTEXT-AWARE CREW DETECTION - Check for crew context indicators
      const localContext = this.getContextWindow(text, match, 100);
      const crewContextIndicators = /\b(crew|crews)\b/i;
      const fireContextIndicators = /\b(fire|fires|containment|response|burn|smoke|hotspot|wildfire)\b/i;

      // Check if this appears to be in crew context AND is a small number (likely crew)
      const fireNumberInt = parseInt(fireNumber);
      const hasCrewContext = crewContextIndicators.test(localContext);
      const hasFireContext = fireContextIndicators.test(localContext);

      // Check if this matches known crew identifier patterns from dictionary
      const crewIdentifiers = this.abbreviations.crew_identifiers || {};
      const isKnownCrewPrefix = Object.keys(crewIdentifiers).includes(regionCode);

      // ENHANCED SMART LOGIC: 
      // 1. If explicit "crew" context AND small number (≤19) AND no strong fire context, likely crew
      // 2. If known crew prefix AND single/double digit number (≤19) AND no fire context, likely crew
      if ((hasCrewContext && fireNumberInt <= 19 && !hasFireContext) ||
        (isKnownCrewPrefix && fireNumberInt <= 19 && !hasFireContext)) {
        return match; // Skip fire enhancement, let crew enhancement handle it
      }

      // LOCAL context analysis - only around THIS fire code
      const inferredYear = this.inferFireYearLocal(localContext, match, logYear);

      const standardizedNumber = fireNumber.padStart(3, '0');
      const yearSuffix = String(inferredYear).slice(-2);
      const enhanced = `${regionCode}${standardizedNumber}-${yearSuffix}`;

      return enhanced;
    }, changes, {
      type: 'fire_code_standardization',
      description: (orig, repl) => {
        const regionName = this.getRegionName(orig.substring(0, 2));
        return `Standardized ${regionName} fire code: ${orig} → ${repl}`;
      }
    });

    // Handle "Fire #" patterns  
    const fireNumberPattern = /\bFire\s+(\d{1,3})\b/gi;
    result = this.safeReplace(result, fireNumberPattern, (match, number) => {
      const regionCode = this.inferRegionFromContext(result, match);
      if (regionCode) {
        const standardizedNumber = number.padStart(3, '0');
        const yearSuffix = String(logYear).slice(-2);
        return `${regionCode}${standardizedNumber}-${yearSuffix}`;
      }
      return match;
    }, changes, {
      type: 'fire_number_standardization',
      description: (orig, repl) => `Standardized fire reference: ${orig} → ${repl}`
    });

    return result;
  }

  /**
   * Infer fire year based on LOCAL context clues around specific fire code
   */
  private inferFireYearLocal(localContext: string, fireCode: string, logYear: number): number {
    const lastYearPatterns = [
      'burn from last year',
      'fire from last year',
      'last year\'s',
      'last year fire'
    ];

    // Only check patterns in the LOCAL context window around this specific fire
    for (const pattern of lastYearPatterns) {
      if (localContext.includes(pattern)) {
        return logYear - 1;
      }
    }

    return logYear;
  }

  /**
   * Get region name from code
   */
  private getRegionName(regionCode: string): string {
    return this.abbreviations.region_codes[regionCode] || regionCode;
  }

  /**
   * Get text context window around a match (includes bracketed content for context)
   */
  private getContextWindow(text: string, match: string, windowSize: number): string {
    const index = text.indexOf(match);
    if (index === -1) return '';

    const start = Math.max(0, index - windowSize);
    const end = Math.min(text.length, index + match.length + windowSize);

    return text.substring(start, end);
  }

  /**
   * Infer region code from context (reads full text including brackets)
   */
  private inferRegionFromContext(context: string, fireReference: string): string | null {
    // Look for region indicators in the context
    const regionPatterns = [
      { pattern: /\b(EV|Beaufort|Inuvik)\b/i, code: 'EV' },
      { pattern: /\b(VQ|Sahtu|Norman Wells)\b/i, code: 'VQ' },
      { pattern: /\b(SM|South Slave|Fort Smith)\b/i, code: 'SM' },
      { pattern: /\b(ZF|North Slave|Yellowknife)\b/i, code: 'ZF' },
      { pattern: /\b(FS|Dehcho|Fort Simpson)\b/i, code: 'FS' },
      { pattern: /\b(WB|Wrigley)\b/i, code: 'WB' }
    ];

    for (const { pattern, code } of regionPatterns) {
      if (pattern.test(context)) {
        return code;
      }
    }

    return null;
  }

  /**
   * PRIORITY 1: Enhance TDO call fire number context detection (HANDOFF REQUIREMENT)
   * Pattern: "TDO Call - South Slave - 2 new fires found by TPU 28 and 27" → SS028-25, SS027-25
   */
  private enhanceTDOCallFireNumbers(text: string, changes: EnhancementChange[], logYear: number): string {
    let result = text;

    // Pattern for TDO calls with regional context and bare fire numbers
    // Example: "TDO Call - South Slave - fires found by TPU 28 and 27"
    const tdoCallPattern = /\bTDO\s+Call[^.]*?-\s*([^-\n]+)\s*-[^.]*?(\w+)\s+(\d{1,3}(?:\s+and\s+\d{1,3})*)\b/gi;

    result = this.safeReplace(result, tdoCallPattern, (match, regionText, aircraftCallsign, fireNumbers) => {
      // Determine region code from regional context
      const regionCode = this.inferRegionCodeFromText(regionText.trim());

      if (!regionCode) {
        return match; // Can't enhance without region context
      }

      // Extract individual fire numbers (handle "28 and 27" patterns)
      const numbers = fireNumbers.match(/\d{1,3}/g) || [];

      // Build enhanced version with standardized fire codes
      let enhancedMatch = match;

      for (const number of numbers) {
        const standardizedNumber = number.padStart(3, '0');
        const yearSuffix = String(logYear).slice(-2);
        const fireCode = `${regionCode}${standardizedNumber}-${yearSuffix}`;

        // Replace the bare number with the full fire code
        const numberPattern = new RegExp(`\\b${number}\\b`, 'g');
        enhancedMatch = enhancedMatch.replace(numberPattern, `${number} [${fireCode}]`);
      }

      return enhancedMatch;
    }, changes, {
      type: 'tdo_call_fire_number_context',
      description: (orig, repl) => `Enhanced TDO call fire numbers with regional context`
    });

    return result;
  }

  /**
   * Helper: Infer region code from regional text context
   */
  private inferRegionCodeFromText(regionText: string): string | null {
    const regionMappings = [
      { patterns: ['South Slave', 'SS', 'Fort Smith', 'Hay River'], code: 'SM' },
      { patterns: ['North Slave', 'NS', 'ZF', 'Yellowknife'], code: 'ZF' },
      { patterns: ['Dehcho', 'Deh Cho', 'FS', 'Fort Simpson'], code: 'FS' },
      { patterns: ['Beaufort Delta', 'BD', 'EV', 'Inuvik'], code: 'EV' },
      { patterns: ['Sahtu', 'VQ', 'Norman Wells'], code: 'VQ' },
      { patterns: ['Wood Buffalo', 'WB', 'Wrigley'], code: 'WB' }
    ];

    for (const { patterns, code } of regionMappings) {
      for (const pattern of patterns) {
        if (regionText.toLowerCase().includes(pattern.toLowerCase())) {
          return code;
        }
      }
    }

    return null;
  }

  // More methods to follow - this is getting long, should I continue with the remaining methods?
  // Including: enhanceBDCodes, enhanceCrewIdentifiers, enhanceAircraftReferences, etc.

  /**
   * Extract tags based on ENHANCED TEXT analysis for legal discovery and operations
   */
  private extractTags(text: string, tags: Set<string>, tagRationales: TagRationale[], logClass?: string, originalText?: string): void {
    // Enhanced tag set that automatically tracks rationales
    const enhancedTags = {
      add: (tag: string, reason?: string, pattern?: string) => {
        if (!tags.has(tag)) {
          tags.add(tag);
          // Auto-generate reason if not provided
          const autoReason = reason || this.generateTagReason(tag, text);
          tagRationales.push({ tag, reason: autoReason, pattern });
        }
        return enhancedTags;
      },
      has: (tag: string) => tags.has(tag)
    };

    // Aircraft operations
    if (/\b(Bird Dog|Helicopter|Fixed Wing|R\/W|F\/W|aircraft|A\/C|flying|landed|departed|Tanker Group|802s|215s|415s)\b/i.test(text)) {
      enhancedTags.add('AIRCRAFT', 'Detected aircraft terminology or operations');
    }

    // Fire incidents
    if (/\b(active fire|wildfire|hot spot|cold trail|burn|smoke patrol|actual fire|fire call|fire response|\w{2}\d{3}-\d{2})\b/i.test(text) &&
      !/\b(fire monitoring|fire camera|fire base|fire station|fire prevention)\b/i.test(text)) {
      enhancedTags.add('FIRE_INCIDENT', 'Detected fire incident terminology or fire codes');
    }

    // Weather events
    if (/\b(lightning|thunder|precipitation|rain|snow|wind|weather|storm|front|pressure)\b/i.test(text)) {
      enhancedTags.add('WEATHER', 'Detected weather-related terminology');
    }

    // Crew operations
    if (/\b(Beaufort Delta Crew|South Slave Crew|North Slave Crew|Deh Cho Crew|Sahtu Crew|crew)\b/i.test(text)) {
      enhancedTags.add('CREW_OPERATIONS', 'Detected crew identifiers or operations');
    }

    // Logistics
    if (/\b(base|demob|roster|equipment|fuel|cache|staging)\b/i.test(text)) {
      enhancedTags.add('LOGISTICS');
    }

    // Context-sensitive demobilization detection: demob + region = incident conclusion
    const demobPattern = /\b(demob|demobilization|demobing|demobed)\b/i;
    const regionPattern = /\b(region|regions|home region|home regions)\b/i;
    if (demobPattern.test(text) && regionPattern.test(text)) {
      enhancedTags.add('OPERATIONS'); // Incident phase change
      enhancedTags.add('PERSONNEL');  // Staff movements
    }

    // Fuel Logistics - specific aviation fuel operations
    if (/\b(Jet A|Avgas|100LL|R\/W|aviation fuel|drums?|move.*drums?|fuel.*move|EMBER.*fuel|rotary wing)\b/i.test(text)) {
      enhancedTags.add('FUEL_LOGISTICS');
      enhancedTags.add('LOGISTICS'); // Also general logistics
    }

    // Personnel Management - actual personnel operations and assignments
    if (/\b(Regional Duty Officer|Territorial Duty Officer|Incident Commander|duty officer|personnel assigned|crew deployment|staff rotation|personnel briefed)\b/i.test(text)) {
      enhancedTags.add('PERSONNEL', 'Detected personnel management or duty officer terminology', 'personnel_indicators');
    }

    // Communications - information exchange and notifications
    if (/\b(called.*(?:Officer|TEMO|RDO|TDO)|briefed.*(?:on|about)|notified|contacted|informed|updated)\b/i.test(text)) {
      enhancedTags.add('COMMUNICATIONS', 'Detected communication or notification terminology', 'communication_indicators');
    }

    // Values At Risk - enhanced to include camera sites and infrastructure
    if (/\b(Values At Risk|VAR|cabin|protection|community|camera site|camera|infrastructure|monitoring)\b/i.test(text)) {
      enhancedTags.add('VALUES_AT_RISK', 'Detected values at risk terminology (VAR, protection, community assets)', 'values_at_risk_indicators');
    }

    // TDO Call structure
    if (/\b(TDO Call|duty officer call|conference call)\b/i.test(text)) {
      enhancedTags.add('TDO_CALL');
    }

    // CRITICAL EMERGENCY SITUATIONS - highest priority
    if (/\b(life or death|life and death|critical|emergency|urgent|immediate|mayday)\b/i.test(text)) {
      enhancedTags.add('PRIORITY_URGENT');
    }

    // MEDICAL EMERGENCY OPERATIONS
    if (/\b(medivac|medical evacuation|medevac|casualty|injury|injured|medical emergency|patient|ambulance)\b/i.test(text)) {
      enhancedTags.add('MEDICAL_EMERGENCY', 'Detected medical emergency terminology', 'medical_emergency_indicators');
      enhancedTags.add('SAFETY_CRITICAL', 'Medical emergencies are safety critical operations', 'medical_emergency_safety');
    }

    // PUBLIC SAFETY INCIDENTS
    if (/\b(member of the public|civilian|public safety|rescue|search and rescue|SAR)\b/i.test(text)) {
      enhancedTags.add('PUBLIC_SAFETY', 'Detected public safety incident terminology', 'public_safety_indicators');
    }

    // SAFETY CRITICAL OPERATIONS
    if (/\b(safety|incident|accident|emergency response|crisis|hazard|risk)\b/i.test(text)) {
      enhancedTags.add('SAFETY_CRITICAL', 'Detected safety-critical terminology', 'safety_critical_indicators');
    }

    // Training/Practice operations
    if (/\b(training|practice|drill|exercise|fit test|fitness|WFX|arduous|simulation|demo)\b/i.test(text)) {
      enhancedTags.add('TRAINING');
    }

    // FIRE-SPECIFIC TAGS for legal discovery - extract all standardized fire codes
    const fireCodePattern = /\b(EV|VQ|SM|ZF|FS|WB)(\d{3})-(\d{2})\b/g;
    const fireMatches = [...text.matchAll(fireCodePattern)];
    fireMatches.forEach(match => {
      const fireCode = match[0]; // e.g., "FS002-24", "EV003-25"
      enhancedTags.add(`FIRE_${fireCode}`, `Detected standardized fire code in text: ${fireCode}`, 'fire_code_pattern');
    });

    // REGIONAL TAGS - Based on content analysis (for non-regional logClass entries)
    this.extractRegionalTags(text, tags, logClass);

    // IMPORT/EXPORT TAGS for inter-jurisdictional resource tracking (not transportation)
    if (/\b(import|importing)\s+(crew|resource|personnel|equipment)\b/i.test(text) ||
      /\bcrew\s+import\b/i.test(text) ||
      /\bresource\s+import\b/i.test(text)) {
      enhancedTags.add('IMPORT', 'Detected inter-jurisdictional resource import terminology', 'import_resource_indicators');
      enhancedTags.add('CREW_OPERATIONS', 'Import involves crew movement operations', 'import_crew_context');
    }

    if (/\b(export|exporting)\s+(crew|resource|personnel|equipment)\b/i.test(text) ||
      /\bcrew\s+export\b/i.test(text) ||
      /\bresource\s+export\b/i.test(text) ||
      /\bavailable\s+for\s+export\b/i.test(text)) {
      enhancedTags.add('EXPORT', 'Detected inter-jurisdictional resource export terminology', 'export_resource_indicators');
      enhancedTags.add('CREW_OPERATIONS', 'Export involves crew movement operations', 'export_crew_context');
    }

    // Weather operations
    if (/\b(weather|wind|temperature|humidity|precipitation|forecast)\b/i.test(text)) {
      enhancedTags.add('WEATHER', 'Detected weather-related terminology', 'weather_indicators');
    }

    // Medical/Safety incidents - enhanced to include operational safety decisions
    if (/\b(injury|medical|safety|evacuation|medevac|accident|pulled off|withdrawn|stood down|evacuated|pulled back|safety concern|unsafe conditions)\b/i.test(text)) {
      enhancedTags.add('MEDICAL_SAFETY');
    }

    // Operational Safety - crew safety decisions due to conditions
    if (/\b(crews? (pulled off|withdrawn|stood down|evacuated|pulled back)|due to (increasing|strong|high) winds?|unsafe (conditions|weather)|safety (concern|issue))\b/i.test(text)) {
      enhancedTags.add('OPERATIONAL_SAFETY');
    }

    // Equipment/Maintenance
    if (/\b(maintenance|repair|service|equipment|mechanical|breakdown)\b/i.test(text)) {
      enhancedTags.add('EQUIPMENT_MAINTENANCE');
    }

    // Finance/Costs - detect pricing, costs, and financial content
    const financeIndicators = this.rules.tagging_rules?.finance_indicators || [];
    const financePattern = new RegExp('\\b(' + financeIndicators.join('|') + ')\\b', 'i');
    if (financePattern.test(text)) {
      enhancedTags.add('FINANCE');
    }

    // Interjurisdictional resources - detect cross-jurisdictional resource sharing
    const interjurisdictionalIndicators = this.rules.tagging_rules?.interjurisdictional_indicators || [];
    const interjurisdictionalPattern = new RegExp('\\b(' + interjurisdictionalIndicators.join('|') + ')\\b', 'i');
    //  console.log(`Checking for interjurisdictional resources :`,interjurisdictionalPattern);

    // Test original text instead of enhanced text to avoid false positives from aircraft enhancements
    const textToTest = originalText || text; // Fallback to enhanced text if original not provided
    if (interjurisdictionalPattern.test(textToTest)) {
      console.log(`Detected interjurisdictional resources in text: ${textToTest}`);
      enhancedTags.add('IMPORT');
      enhancedTags.add('CREW_OPERATIONS'); // Cross-jurisdictional involves crew movement
    }

    // Internal crew imports - detect crews from other NWT regions working in host region
    if (logClass) {
      const hostRegionCode = this.determineHostRegionFromLogClass(logClass);
      if (hostRegionCode) {
        // Check for crew codes from other regions
        const crewRegionMappings = {
          'BD': 'EV', // Beaufort Delta 
          'SS': 'SM', // South Slave
          'NS': 'ZF', // North Slave  
          'DC': 'FS', // Deh Cho
          'Sahtu': 'VQ' // Sahtu
        };

        for (const [crewPrefix, crewRegionCode] of Object.entries(crewRegionMappings)) {
          if (crewRegionCode !== hostRegionCode && new RegExp(`\\b${crewPrefix}\\d{1,2}\\b`, 'i').test(text)) {
            enhancedTags.add('IMPORT');
            enhancedTags.add('CREW_OPERATIONS');
            break; // Found at least one external crew
          }
        }
      }
    }
  }

  /**
   * Extract regional tags based on content analysis (for non-regional logClass entries)
   * Detects regions from fire codes, locations, crews, and other regional indicators
   */
  private extractRegionalTags(text: string, tags: Set<string>, logClass?: string): void {
    const regions = new Set<string>();

    // 1. DIRECT FIRE CODE DETECTION - Most reliable indicator
    const fireCodePattern = /\b(EV|VQ|SM|ZF|FS|WB)\d{3}(-\d{2})?\b/g;
    const fireMatches = [...text.matchAll(fireCodePattern)];
    fireMatches.forEach(match => {
      const regionCode = match[1];
      regions.add(regionCode);
    });

    // 2. LOCATION-BASED DETECTION 
    const locationCodes = this.abbreviations.location_codes || {};
    const regionCodes = this.abbreviations.region_codes || {};

    // Check for location codes that map to regions
    Object.keys(locationCodes).forEach(locationCode => {
      if (new RegExp(`\\b${locationCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(text)) {
        // Map location to region
        if (regionCodes[locationCode]) {
          regions.add(locationCode);
        }
      }
    });

    // 3. COMMUNITY-BASED DETECTION
    const communities = this.abbreviations.communities || {};
    Object.entries(communities).forEach(([regionKey, communityList]) => {
      const regionCode = this.mapRegionKeyToCode(regionKey);
      if (regionCode && Array.isArray(communityList)) {
        communityList.forEach(community => {
          if (new RegExp(`\\b${community.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(text)) {
            regions.add(regionCode);
          }
        });
      }
    });

    // SPECIFIC DEHCHO PLACENAME DETECTION (for testing)
    const dehchoPlaces = ['Netla', 'Little Doctor'];
    dehchoPlaces.forEach(place => {
      if (new RegExp(`\\b${place.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(text)) {
        regions.add('FS'); // Force Dehcho region code
      }
    });

    // 4. SMART CREW IDENTIFIER DETECTION - Context-aware import detection
    const crewIdentifiers = this.abbreviations.crew_identifiers || {};
    const hostRegionCode = this.determineHostRegionFromLogClass(logClass);

    Object.entries(crewIdentifiers).forEach(([crewCode, regionName]) => {
      if (new RegExp(`\\b${crewCode}\\b`, 'i').test(text)) {
        const crewRegionCode = this.mapRegionNameToCode(regionName);
        if (crewRegionCode) {
          // Smart logic: Only add regional tag if this is the HOST region
          // If crew is from different region than host, it's an IMPORT, not regional content
          if (!hostRegionCode || crewRegionCode === hostRegionCode) {
            regions.add(crewRegionCode);
          }
          // Note: Import tagging happens separately in extractTags method
        }
      }
    });

    // 5. REGIONAL DUTY OFFICER DETECTION
    const personnelRoles = this.abbreviations.personnel_roles || {};
    Object.entries(personnelRoles).forEach(([roleCode, roleDescription]) => {
      if (new RegExp(`\\b${roleCode}\\b`, 'i').test(text)) {
        // Extract region from role descriptions like "Regional Duty Officer Dehcho"
        if (roleDescription.includes('Dehcho')) regions.add('FS');
        else if (roleDescription.includes('Beaufort Delta')) regions.add('EV');
        else if (roleDescription.includes('North Slave')) regions.add('ZF');
        else if (roleDescription.includes('South Slave')) regions.add('SM');
        else if (roleDescription.includes('Sahtu')) regions.add('VQ');
      }
    });

    // 6. REGION ALIAS DETECTION
    const regionAliases = this.abbreviations.region_aliases || {};
    Object.entries(regionAliases).forEach(([alias, regionCode]) => {
      if (new RegExp(`\\b${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(text)) {
        regions.add(regionCode);
      }
    });

    // Convert detected regions to descriptive tags (expand codes to full names)
    regions.forEach(regionCode => {
      const regionName = this.abbreviations.region_codes[regionCode];
      if (regionName) {
        // Use descriptive region name only (expand shorthand to full description)
        tags.add(`REGION_${regionName.replace(/\s+/g, '_').toUpperCase()}`); // e.g., REGION_DEHCHO, REGION_BEAUFORT_DELTA
      }
    });
  }

  /**
   * Helper: Map community region keys to region codes
   */
  private mapRegionKeyToCode(regionKey: string): string | null {
    const mapping: { [key: string]: string } = {
      'Deh_Cho': 'FS',
      'Sahtu': 'VQ',
      'Beaufort_Delta': 'EV',
      'North_Slave': 'ZF',
      'South_Slave': 'SM'
    };
    return mapping[regionKey] || null;
  }

  /**
   * Helper: Map region names to region codes
   */
  private mapRegionNameToCode(regionName: string): string | null {
    const regionCodes = this.abbreviations.region_codes || {};
    const entry = Object.entries(regionCodes).find(([code, name]) =>
      name.toLowerCase().includes(regionName.toLowerCase()) ||
      regionName.toLowerCase().includes(name.toLowerCase())
    );
    return entry ? entry[0] : null;
  }

  /**
   * Determine the host region code from logClass context
   */
  private determineHostRegionFromLogClass(logClass?: string): string | null {
    if (!logClass) return null;

    // Pattern 1: "Regional_XX" format
    const regionalCodeMatch = logClass.match(/Regional[_\-\s]*([A-Z]{2})\b/i);
    if (regionalCodeMatch) {
      return regionalCodeMatch[1].toUpperCase();
    }

    // Pattern 2: "XX_Regional" format  
    const codeRegionalMatch = logClass.match(/([A-Z]{2})[_\-\s]*Regional/i);
    if (codeRegionalMatch) {
      return codeRegionalMatch[1].toUpperCase();
    }

    // Pattern 3: Full region name in logClass
    const regionCodes = this.abbreviations.region_codes || {};
    for (const [code, regionName] of Object.entries(regionCodes)) {
      if (logClass.toLowerCase().includes(regionName.toLowerCase())) {
        return code.toUpperCase();
      }
    }

    return null;
  }

  /**
   * Extract regional tags directly from logClass (for regional log classifications)
   */
  private extractRegionalTagsFromLogClass(logClass: string, tags: Set<string>, tagRationales: TagRationale[]): void {
    // Enhanced tag set that automatically tracks rationales
    const enhancedTags = {
      add: (tag: string, reason?: string, pattern?: string) => {
        if (!tags.has(tag)) {
          tags.add(tag);
          const autoReason = reason || this.generateTagReason(tag, logClass);
          tagRationales.push({ tag, reason: autoReason, pattern });
        }
        return enhancedTags;
      },
      has: (tag: string) => tags.has(tag)
    };

    // Direct regional logClass format examples: 
    // "Regional_FS", "Regional_Dehcho", "FS_Regional", etc.

    // Extract region code from logClass
    let regionCode: string | null = null;
    let regionName: string | null = null;

    // Pattern 1: "Regional_XX" format
    const regionalCodeMatch = logClass.match(/Regional[_\-\s]*([A-Z]{2})\b/i);
    if (regionalCodeMatch) {
      regionCode = regionalCodeMatch[1].toUpperCase();
    }

    // Pattern 2: "XX_Regional" format  
    const codeRegionalMatch = logClass.match(/\b([A-Z]{2})[_\-\s]*Regional/i);
    if (codeRegionalMatch) {
      regionCode = codeRegionalMatch[1].toUpperCase();
    }

    // Pattern 3: Region name in logClass (e.g., "Regional_Dehcho", "Beaufort_Delta_Regional")
    const regionNames = Object.entries(this.abbreviations.region_codes || {});
    for (const [code, name] of regionNames) {
      const namePattern = new RegExp(name.replace(/\s+/g, '[_\\-\\s]*'), 'i');
      if (namePattern.test(logClass)) {
        regionCode = code;
        regionName = name;
        break;
      }
    }

    // Add regional tags if found (use descriptive names, expand codes to full descriptions)
    if (regionCode) {
      const fullRegionName = this.abbreviations.region_codes[regionCode];
      if (fullRegionName) {
        // Use descriptive region name only (expand shorthand to full description)
        enhancedTags.add(`REGION_${fullRegionName.replace(/\s+/g, '_').toUpperCase()}`, `Log class indicates ${fullRegionName} region: ${logClass}`);
        enhancedTags.add('REGIONAL_LOG', `Classified as regional log from log class: ${logClass}`);
      }
    }
  }

  /**
   * Enhanced BD code disambiguation with SPACE HANDLING and context detection
   */
  private enhanceBDCodes(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // FIXED: Handle spaces in BD codes "BD 104" and "BD104"
    const bdPattern = /\bBD\s*(\d+)\b/g;

    // Aircraft context indicators (reads full text including brackets for context)
    const aircraftContext = /\b(helicopter|heli|aircraft|A\/C|flying|landed|departed|inbound|outbound|working|tanker|group)\b/i;

    result = this.safeReplace(result, bdPattern, (match, number) => {
      const digits = parseInt(number);

      // Check surrounding context for aircraft indicators
      const surroundingText = this.getContextWindow(text, match, 75);
      const hasAircraftContext = aircraftContext.test(surroundingText);

      // BD disambiguation logic based on digits and context
      if (digits >= 100 || hasAircraftContext) {
        // 3+ digits OR aircraft context = Bird Dog aircraft
        return `BD${number} [Bird Dog ${number}]`;
      } else {
        // 1-2 digits AND no aircraft context = Beaufort Delta Crew
        return `BD${number} [Beaufort Delta Crew ${number}]`;
      }
    }, changes, {
      type: 'bd_enhancement',
      description: (orig, repl) => `Enhanced BD identifier: ${orig} → crew/aircraft based on digits and context`
    });

    // Handle standalone "BD" (without numbers) - should be Beaufort Delta region
    const standaloneBdPattern = /\bBD\b(?!\s*\d)/g;
    result = this.safeReplace(result, standaloneBdPattern, (match) => {
      return `${match} [Beaufort Delta]`;
    }, changes, {
      type: 'bd_region_enhancement',
      description: (orig, repl) => `Enhanced standalone BD: ${orig} → Beaufort Delta region`
    });

    return result;
  }

  /**
   * Enhanced crew identifier expansion using external dictionary
   */
  private enhanceCrewIdentifiers(text: string, changes: EnhancementChange[]): string {
    let result = text;
    // Targeted: Standardize 'Deh Cho X' to 'DCX [Deh Cho Crew X]' for X=1-19
    result = result.replace(/\bDeh\s*Cho\s*(\d{1,2})\b/gi, (match, number) => {
      const crewNum = parseInt(number);
      if (crewNum >= 1 && crewNum <= 19) {
        return `DC${crewNum} [Deh Cho Crew ${crewNum}]`;
      }
      return match;
    });
    //let result = text;

    // Use external crew identifiers dictionary
    const crewIdentifiers = this.abbreviations.crew_identifiers || {};
    // Build a reverse lookup: region name → region code
    const regionNameToCode: Record<string, string> = {};
    for (const [code, name] of Object.entries(crewIdentifiers)) {
      regionNameToCode[name.toLowerCase()] = code;
    }

    for (const [prefix, region] of Object.entries(crewIdentifiers)) {
      // Skip BD as it's handled separately in enhanceBDCodes
      if (prefix === 'BD') continue;

      // Handle both regular prefixes and special cases like "Sahtu" and "Dehcho"
      let pattern: RegExp;
      if (prefix.length > 2) { // "Sahtu", "Dehcho"
        pattern = new RegExp('\\b(' + prefix + ')\\s*(\\d{1,2})\\b', 'g');
      } else { // "SS", "NS", "DC" - 1-2 digits, max 19
        pattern = new RegExp('\\b(' + prefix + ')(\\d{1,2})\\b', 'g');
      }

      result = this.safeReplace(result, pattern, (match, prefixMatch, number) => {
        const crewNumber = parseInt(number);
        if (crewNumber <= 19) {
          // Always use region code in output
          let regionCode = prefix;
          let regionName = region;
          // If prefix is a region name, get its code
          if (prefix.length > 2) {
            // Find region code for this region name
            regionCode = regionNameToCode[prefixMatch.toLowerCase()] || regionNameToCode[regionName.toLowerCase()] || regionCode;
          }
          // Standardize output: always use region code as prefix
          return `${regionCode}${number} [${regionName} Crew ${number}]`;
        }
        return match;
      }, changes, {
        type: 'crew_identifier_enhancement',
        description: (orig, repl) => `Enhanced crew identifier: ${orig} → ${region} Crew`
      });
    }

    // Additional: Replace region name + crew number (with or without space) with region code prefix
    for (const [regionName, regionCode] of Object.entries(regionNameToCode)) {
      // Match 'Dehcho1', 'dechcho1', 'Deh Cho 1', 'deh cho 1' (with and without space, case-insensitive)
      console.log(`[### BREADCRUMB] Matching region name: ${regionName} with code: ${regionCode}`);
      let variants = [regionName, regionName.replace(/\s+/g, ''), regionName.replace(/\s+/g, ' ')];
      variants.forEach(variant => {
        // No space variant (e.g., 'Dehcho1', 'dechcho1'), skip if already followed by [Crew ...] or [Deh Cho Crew ...]
        let patternNoSpace = new RegExp(`\b${variant.replace(/\s+/g, '')}(\d{1,2})(?!\s*\[.*Crew.*\])\b`, 'gi');
        result = result.replace(patternNoSpace, (_match, number) => {
          if (/\[Deh Cho Crew/.test(_match)) return _match;
          if (parseInt(number) <= 19) {
            const regionFullName = crewIdentifiers[regionCode];
            return `${regionCode}${number} [${regionFullName} Crew ${number}]`;
          }
          return `${_match}`;
        });
        // With space variant (e.g., 'Deh Cho 1', 'deh cho 1'), skip if already followed by [Crew ...] or [Deh Cho Crew ...]
        let patternWithSpace = new RegExp(`\b${variant.replace(/\s+/g, ' ')}\s+(\d{1,2})(?!\s*\[.*Crew.*\])\b`, 'gi');
        result = result.replace(patternWithSpace, (_match, number) => {
          if (/\[Deh Cho Crew/.test(_match)) return _match;
          if (parseInt(number) <= 19) {
            const regionFullName = crewIdentifiers[regionCode];
            return `${regionCode}${number} [${regionFullName} Crew ${number}]`;
          }
          return `${_match}`;
        });
      });
    }
    return result;

    return result;
  }

  /**
   * Enhanced aircraft reference detection and expansion
   */
  private enhanceAircraftReferences(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // FIRST PRIORITY: Bird Dog pattern detection
    const birdDogPattern = /\bBird Dog\s+(\d{2,3})\b/gi;
    result = this.safeReplace(result, birdDogPattern, (match, number) => {
      return `${match} [Bird Dog ${number} Aircraft]`;
    }, changes, {
      type: 'bird_dog_aircraft_enhancement',
      description: (orig, repl) => `Enhanced Bird Dog aircraft: ${orig} → Bird Dog aircraft`
    });

    // R/W and F/W expansion
    result = this.safeReplace(result, /\bR\/W\b/g, (match) => {
      return `${match} [Helicopter]`;
    }, changes, {
      type: 'aircraft_type_expansion',
      description: (orig, repl) => 'Expanded R/W to Helicopter'
    });

    result = this.safeReplace(result, /\bF\/W\b/g, (match) => {
      return `${match} [Fixed Wing]`;
    }, changes, {
      type: 'aircraft_type_expansion',
      description: (orig, repl) => 'Expanded F/W to Fixed Wing'
    });

    // Context detection patterns
    const aviationContext = /\b(aircraft|A\/C|working|flying|landed|departed|inbound|outbound|down|clear|takeoff|approach)\b/i;
    const airportContext = /\b(airport|runway|tower|approach|YJF|YZF|YEV|YVQ|YSM|YFS)\b/i;
    const baseContext = /\b(base|fire base|staging|sling|hover|landing|LZ)\b/i;

    // Known specific aircraft codes from dictionary - FIRST PRIORITY
    const aircraftCodes = this.abbreviations.aircraft_codes || {};

    // Add known aircraft codes from rules
    const knownAircraftCodes = this.rules.aircraft_callsign_patterns?.known_aircraft_codes || {};
    const allAircraftCodes = { ...aircraftCodes, ...knownAircraftCodes };

    for (const [code, description] of Object.entries(allAircraftCodes)) {
      if (code.length >= 3 && code !== 'R/W' && code !== 'F/W') {
        const pattern = new RegExp('\\b' + code + '\\b', 'g');
        result = this.safeReplace(result, pattern, (match) => {
          return `${match} [${description}]`;
        }, changes, {
          type: 'known_aircraft_expansion',
          description: (orig, repl) => `Enhanced known aircraft: ${orig} → ${description}`
        });
      }
    }

    // Enhanced helicopter context detection for WTQ and other codes
    const helicopterContextIndicators = this.rules.aircraft_callsign_patterns?.helicopter_context_indicators || ['rw', 'R/W', 'rotary wing', 'helicopter', 'heli', 'switch', 'swith'];
    const helicopterContextPattern = new RegExp('\\b(' + helicopterContextIndicators.join('|') + ')\\b', 'i');

    // Check for helicopter context patterns
    result = this.safeReplace(result, /\b([A-Z]{3})\b/g, (match, callsign) => {
      // Skip if already enhanced
      if (this.isAlreadyEnhanced(result, callsign)) return match;

      // Check if this callsign appears near helicopter context indicators
      const surroundingText = this.getContextWindow(text, match, 50);
      if (helicopterContextPattern.test(surroundingText) && !allAircraftCodes[callsign]) {
        return `${callsign} [Helicopter ${callsign}]`;
      }

      return match;
    }, changes, {
      type: 'helicopter_context_enhancement',
      description: (orig, repl) => `Enhanced helicopter with context: ${orig} → helicopter context`
    });

    // Unknown 3-letter callsigns with intelligent context detection
    const threeLetterPattern = /\b([A-Z]{3})\b/g;
    result = this.safeReplace(result, threeLetterPattern, (match, callsign) => {
      // Skip if already enhanced by known aircraft codes
      if (this.isAlreadyEnhanced(result, callsign)) return match;

      const surroundingText = this.getContextWindow(text, match, 50);

      // Check for aviation context first
      if (aviationContext.test(surroundingText)) {
        // Within aviation context, determine aircraft type
        if (airportContext.test(surroundingText)) {
          return `Fixed Wing ${callsign}`;
        } else if (baseContext.test(surroundingText)) {
          return `Helicopter ${callsign}`;
        } else {
          // General aviation context - use generic aircraft designation
          return `Aircraft ${callsign}`;
        }
      }

      return match;
    }, changes, {
      type: 'intelligent_aircraft_callsign_enhancement',
      description: (orig, repl) => `Enhanced aircraft callsign with context: ${orig} → ${repl}`
    });

    // TLA Heuristic: All pure alphabetical TLAs are aircraft identifiers
    // Handle both regular TLAs and possessive forms (SSK's, WTQ's, etc.)
    const tlaPattern = /\b([A-Z]{3})('s)?\b/g;
    result = this.safeReplace(result, tlaPattern, (match, callsign, possessive) => {
      // Skip if already enhanced by previous aircraft enhancement
      if (this.isAlreadyEnhanced(result, callsign)) return match;

      // All pure alphabetical TLAs are aircraft - could be Fixed Wing or Helicopter
      // Since we can't determine which from context alone, defer to generic "aircraft"
      const possessiveForm = possessive || '';
      return `${callsign}${possessiveForm} [${callsign} (aircraft)]`;
    }, changes, {
      type: 'tla_heuristic_enhancement',
      description: (orig, repl) => `Enhanced TLA as aircraft: ${orig} → aircraft identifier`
    });

    return result;
  }

  /**
   * Comprehensive geographic reference enhancement with FIXED PRIORITY and duplicate prevention
   */
  private enhanceGeographicReferences(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Get all geographic dictionaries with CORRECT PRIORITY ORDER
    const locationCodes = this.abbreviations.location_codes || {};
    const provinceCodes = this.abbreviations.province_codes || {};
    const areaCodes = this.abbreviations.area_codes || {};
    const districtCodes = this.abbreviations.district_codes || {};
    const regionAliases = this.abbreviations.region_aliases || {};

    // 0. AIR TANKER BASE PATTERN MATCHING - [LocationCode]ATB
    const atbPattern = /\b([A-Z]{2,4})ATB\b/g;
    result = this.safeReplace(result, atbPattern, (match, locationCode) => {
      // Look up the location name
      const locationName = locationCodes[locationCode];
      if (locationName) {
        return `${match} [${locationName} Air Tanker Base]`;
      }
      return match; // No enhancement if location code not found
    }, changes, {
      type: 'air_tanker_base_pattern',
      description: (orig, repl) => `Enhanced Air Tanker Base pattern: ${orig} → location facility`
    });

    // 1. MOVEMENT CONTEXT - highest priority, most specific
    const movementPattern = /\b(to|at|from|in|for|going to|heading to|inbound|outbound|departed|arrived|landing|landed)\s+([A-Z]{2,4})\b/gi;
    result = this.safeReplace(result, movementPattern, (match, preposition, code) => {
      // Context-aware prioritization: Check for import/export and aviation context
      const importExportContext = /\b(import|export|available for export|crews? from [A-Z]{2}|resource|deployment|inter-jurisdictional)\b/i.test(text);
      const aviationContext = /\b(landed|departed|down|clear|base|airport|runway|flight|aircraft|helicopter|takeoff|approach)\b/i.test(text);

      // Check dictionaries with CONTEXT-SENSITIVE PRIORITY ORDER
      const locationName = locationCodes[code];
      const provinceName = provinceCodes[code];
      const areaName = areaCodes[code];
      const districtName = districtCodes[code];

      // Special handling for JF with aviation context
      if (code === 'JF') {
        if (aviationContext) {
          return `${preposition} ${code} [Fort Liard Airport]`;
        } else {
          return `${preposition} ${code} [Fort Liard]`;
        }
      } else if (importExportContext && provinceName) {
        // In import/export context, prioritize provinces
        return `${preposition} ${code} [${provinceName}]`;
      } else if (locationName) {
        // In local operations context, prioritize locations
        return `${preposition} ${code} [${locationName}]`;
      } else if (provinceName) {
        return `${preposition} ${code} [${provinceName}]`;
      } else if (areaName) {
        return `${preposition} ${code} [${areaName}]`;
      } else if (districtName) {
        return `${preposition} ${code} [${districtName}]`;
      }
      return match;
    }, changes, {
      type: 'geographic_movement_enhancement',
      description: (orig, repl) => `Enhanced geographic reference in movement context: ${orig}`
    });

    // 2. STANDALONE GEOGRAPHIC CODES - context-aware prioritization  
    const importExportContext = /\b(import|export|available for export|crews? from [A-Z]{2}|resource|deployment|inter-jurisdictional)\b/i.test(text);
    const aviationContext = /\b(landed|departed|down|clear|base|airport|runway|flight|aircraft|helicopter|takeoff|approach)\b/i.test(text);

    // First pass: Handle all codes with context-aware prioritization
    const allGeoCodes = new Set([
      ...Object.keys(locationCodes),
      ...Object.keys(provinceCodes),
      ...Object.keys(areaCodes),
      ...Object.keys(districtCodes)
    ]);

    for (const code of allGeoCodes) {
      if (this.isAlreadyEnhanced(result, code)) continue;

      const personContext = new RegExp('\\b(called|spoke with|briefed|contacted)\\s+' + code + '\\b', 'i');
      if (personContext.test(result)) continue;

      // Context-sensitive lookup
      const locationName = locationCodes[code];
      const provinceName = provinceCodes[code];
      const areaName = areaCodes[code];
      const districtName = districtCodes[code];

      let selectedName = '';
      let selectedType = '';

      // Special handling for JF with aviation context
      if (code === 'JF') {
        if (aviationContext) {
          selectedName = 'Fort Liard Airport';
          selectedType = 'airport_enhancement';
        } else {
          selectedName = 'Fort Liard';
          selectedType = 'location_enhancement';
        }
      } else if (importExportContext && provinceName) {
        selectedName = provinceName;
        selectedType = 'province_enhancement';
      } else if (locationName) {
        selectedName = locationName;
        selectedType = 'location_enhancement';
      } else if (provinceName) {
        selectedName = provinceName;
        selectedType = 'province_enhancement';
      } else if (areaName) {
        selectedName = areaName;
        selectedType = 'area_enhancement';
      } else if (districtName) {
        selectedName = districtName;
        selectedType = 'district_enhancement';
      }

      if (selectedName) {
        const pattern = new RegExp('\\b' + code + '\\b', 'g');
        result = this.safeReplace(result, pattern, (match) => {
          return `${match} [${selectedName}]`;
        }, changes, {
          type: selectedType,
          description: (orig, repl) => `Enhanced geographic code: ${orig} → ${selectedName}`
        });
      }
    }

    return result;
  }

  /**
   * Enhance fire base references with location context
   */
  private enhanceFireBases(text: string, changes: EnhancementChange[]): string {
    let result = text;
    const fireBases = this.abbreviations.fire_bases || {};

    for (const [code, fullName] of Object.entries(fireBases)) {
      const pattern = new RegExp('\\b' + code + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${fullName}]`;
      }, changes, {
        type: 'fire_base_enhancement',
        description: (orig, repl) => `Enhanced fire base reference: ${orig} → ${fullName}`
      });
    }

    return result;
  }

  /**
   * Expand status and response codes - FIXED to handle fire codes between context and status
   */
  private expandStatusCodes(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // FIXED: Status codes with optional fire code in between
    const statusCodes = this.abbreviations.status_codes || {};
    const statusPattern = /\b(called|fire|went|status|declared|reporting|now)\s+(?:[A-Z]{2}\d{3,4}(?:-\d{2})?\s+)?(OC|BH|BM|UC|BP|EX)\b/gi;

    result = this.safeReplace(result, statusPattern, (match, context, code) => {
      const status = statusCodes[code];
      if (status) {
        return match.replace(code, `${code} [${status}]`);
      }
      return match;
    }, changes, {
      type: 'status_code_expansion',
      description: (orig, repl) => `Expanded status code in context`
    });

    // FIXED: Response codes with optional fire code in between
    const responseCodes = this.abbreviations.response_codes || {};
    const responsePattern = /\b(went|status|declared|response)\s+(?:[A-Z]{2}\d{3,4}(?:-\d{2})?\s+)?(AB|DA|IA|LA|SA)\b/gi;

    result = this.safeReplace(result, responsePattern, (match, context, code) => {
      const response = responseCodes[code];
      if (response) {
        return match.replace(code, `${code} [${response}]`);
      }
      return match;
    }, changes, {
      type: 'response_code_expansion',
      description: (orig, repl) => `Expanded response code in context`
    });

    return result;
  }

  /**
   * Enhanced aviation terminology
   */
  private enhanceAviationTerminology(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Machine -> Helicopter pattern
    const machinePattern = /\bmachine\s+([A-Z]{2,3})\b/gi;
    result = this.safeReplace(result, machinePattern, (match, callsign) => {
      return `helicopter ${callsign}`;
    }, changes, {
      type: 'machine_to_helicopter',
      description: (orig, repl) => `Enhanced machine reference: ${orig} → ${repl}`
    });

    // Group references -> Tanker Group (be very specific to avoid self-reference)
    const groupPattern = /\bGroup\s+(\d+)\b/gi;
    result = this.safeReplace(result, groupPattern, (match, number) => {
      return `Group ${number} [Tanker Group ${number}]`;
    }, changes, {
      type: 'aviation_group_enhancement',
      description: (orig, repl) => `Enhanced aviation group: ${orig} → Tanker Group`
    });

    // Fleet designations from dictionary
    const fleetCodes = this.abbreviations.aircraft_fleets || {};
    for (const [code, description] of Object.entries(fleetCodes)) {
      const pattern = new RegExp('\\b' + code + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${description}]`;
      }, changes, {
        type: 'aircraft_fleet_enhancement',
        description: (orig, repl) => `Enhanced aircraft fleet: ${orig} → fleet type`
      });
    }

    // Aviation fuel terminology from dictionary
    const aviationFuel = this.abbreviations.aviation_fuel || {};
    for (const [fuel, description] of Object.entries(aviationFuel)) {
      const pattern = new RegExp('\\b' + fuel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${description}]`;
      }, changes, {
        type: 'aviation_fuel_enhancement',
        description: (orig, repl) => `Enhanced aviation fuel: ${orig} → ${description}`
      });
    }

    return result;
  }

  /**
   * Expand wildfire terminology
   */
  private expandWildfireTerminology(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Special context-sensitive handling for "complex"
    const complexPattern = /\bcomplex\b/gi;
    const firefireContext = /\b(fire|fires|incident|wildfire|burn|smoke|response)\b/i;
    const fireCodeContext = /\b(EV|VQ|SM|ZF|FS|WB)\d{1,4}(-\d{2})?\b/i;

    result = this.safeReplace(result, complexPattern, (match) => {
      const surroundingText = this.getContextWindow(text, match, 50);
      const hasFireContext = firefireContext.test(surroundingText);
      const hasFireCodeInLog = fireCodeContext.test(text); // Check entire log for fire codes

      if (hasFireContext || hasFireCodeInLog) {
        return `${match} [multiple fires managed under a single incident command structure]`;
      }
      return match; // Don't enhance if no fire context
    }, changes, {
      type: 'context_sensitive_complex',
      description: (orig, repl) => `Enhanced wildfire complex term: ${orig} → fire management context`
    });

    // Wildfire terminology (excluding "complex" since it's handled above)
    const terminology = this.abbreviations.wildfire_terminology || {};

    // Debug: Check if demob processing is happening
    if (text.toLowerCase().includes('demob')) {
      console.log('DEBUG: Text contains demob, checking wildfire terminology processing...');
      console.log('DEBUG: terminology object keys:', Object.keys(terminology).slice(0, 15));
      console.log('DEBUG: demob in terminology?', 'demob' in terminology);
      console.log('DEBUG: terminology.demob value:', terminology['demob']);
    }

    for (const [term, expansion] of Object.entries(terminology)) {
      if (term.toLowerCase() === 'complex') continue; // Skip complex, handled above

      // Debug for demob specifically
      if (term === 'demob' && text.toLowerCase().includes('demob')) {
        console.log('DEBUG: Processing demob term...');
        console.log('DEBUG: term value:', term);
        console.log('DEBUG: expansion value:', expansion);
        console.log('DEBUG: isAlreadyEnhanced check:', this.isAlreadyEnhanced(result, term));
      }

      // Skip if already enhanced
      if (this.isAlreadyEnhanced(result, term)) continue;

      const pattern = new RegExp('\\b' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');

      // Debug for demob pattern matching
      if (term === 'demob' && text.toLowerCase().includes('demob')) {
        console.log('DEBUG: demob pattern created:', pattern);
        console.log('DEBUG: pattern test on text:', pattern.test(text));
        console.log('DEBUG: matches found:', text.match(pattern));
      }

      result = this.safeReplace(result, pattern, (match) => {
        // Debug for demob replacement
        if (term === 'demob') {
          console.log('DEBUG: demob safeReplace called with match:', match);
        }

        // Case-insensitive lookup: try original, lowercase, then uppercase
        let foundExpansion = terminology[match];
        if (!foundExpansion) {
          foundExpansion = terminology[match.toLowerCase()];
        }
        if (!foundExpansion) {
          foundExpansion = terminology[match.toUpperCase()];
        }

        if (term === 'demob') {
          console.log('DEBUG: demob lookup results:', {
            original: terminology[match],
            lowercase: terminology[match.toLowerCase()],
            uppercase: terminology[match.toUpperCase()],
            final: foundExpansion
          });
        }

        if (foundExpansion) {
          return `${match} [${foundExpansion}]`;
        }
        return match; // No expansion found
      }, changes, {
        type: 'wildfire_terminology_expansion',
        description: (orig, repl) => `Expanded wildfire term: ${orig} → wildfire terminology`
      });
    }

    // Aircraft classes
    const aircraftClasses = this.abbreviations.aircraft_classes || {};
    for (const [term, expansion] of Object.entries(aircraftClasses)) {
      // Skip if already enhanced
      if (this.isAlreadyEnhanced(result, term)) continue;

      const pattern = new RegExp('\\b' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${expansion}]`;
      }, changes, {
        type: 'aircraft_class_expansion',
        description: (orig, repl) => `Expanded aircraft class: ${orig} → ${expansion}`
      });
    }

    // Equipment codes
    const equipmentCodes = this.abbreviations.equipment_codes || {};
    for (const [code, description] of Object.entries(equipmentCodes)) {
      // Skip if already enhanced
      if (this.isAlreadyEnhanced(result, code)) continue;

      const pattern = new RegExp('\\b' + code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'g');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${description}]`;
      }, changes, {
        type: 'equipment_code_expansion',
        description: (orig, repl) => `Expanded equipment code: ${orig} → ${description}`
      });
    }

    return result;
  }

  /**
   * Enhance Values At Risk (VAR) detection and context
   */
  private enhanceValuesAtRisk(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Values at risk patterns from lookup tables - longest patterns first to prevent overlaps
    const valuesAtRisk = this.abbreviations.values_at_risk || {};
    const sortedTerms = Object.entries(valuesAtRisk).sort((a, b) => b[0].length - a[0].length);

    for (const [term, description] of sortedTerms) {
      // Skip if already enhanced
      if (this.isAlreadyEnhanced(result, term)) continue;

      const pattern = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      result = this.safeReplace(result, pattern, (match) => {
        return `${match} [${description}]`;
      }, changes, {
        type: 'values_at_risk_expansion',
        description: (orig, repl) => `Enhanced Values At Risk: ${orig} → ${description}`
      });
    }

    return result;
  }

  /**
   * Enhance community name references
   */
  private enhanceCommunityReferences(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Get all communities from rules
    const communities = this.rules.community_recognition?.all_communities || [];

    for (const community of communities) {
      // Only enhance if the community name appears to be misspelled or abbreviated
      const variations = [
        community.replace(/\s+/g, ''),  // Remove spaces
        community.substring(0, Math.max(3, community.length - 2))  // Abbreviated
      ];

      for (const variation of variations) {
        if (variation !== community && variation.length >= 3) {
          const pattern = new RegExp('\\b' + variation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
          result = this.safeReplace(result, pattern, (match) => {
            return `${match} [${community}]`;
          }, changes, {
            type: 'community_name_enhancement',
            description: (orig, repl) => `Enhanced community name: ${orig} → ${community}`
          });
        }
      }
    }

    return result;
  }

  /**
   * Fix basic punctuation issues
   */
  private fixPunctuation(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // Fix missing spaces after periods
    result = this.safeReplace(result, /\.([A-Z])/g, (match, letter) => {
      return `. ${letter}`;
    }, changes, {
      type: 'punctuation_fix',
      description: (orig, repl) => `Added space after period: ${orig} → ${repl}`
    });

    // Fix multiple spaces
    result = this.safeReplace(result, /\s{2,}/g, ' ', changes, {
      type: 'spacing_fix',
      description: (orig, repl) => `Fixed multiple spaces`
    });

    return result;
  }

  /**
   * Improve incomplete references
   */
  private improveIncompleteReferences(text: string, changes: EnhancementChange[]): string {
    let result = text;

    // This is a placeholder for future enhancement logic
    // Could include things like completing partial fire references, etc.

    return result;
  }

  /**
   * Enhanced entry point for crosslinking architecture
   * Generates three-field output: body, enhanced_body, enhancement_metadata
   */
  enhanceLogEntryWithCrosslinking(logBody: string, logYear: number = 2025, logClass?: string): CrosslinkEnhancementResult {
    // First get the standard enhancement result
    const standardResult = this.enhanceLogEntry(logBody, logYear, logClass);

    // Initialize metadata structure
    const metadata: EnhancementMetadata = {
      fire_codes: [],
      crews: [],
      aircraft: [],
      general: []
    };

    // Use the enhanced text from standard enhancement as base
    let enhanced_body = standardResult.enhanced_text;

    // Deduplicated collections to prevent multiple entries for same item
    const processedItems = new Set<string>();
    const metadataMap = new Map<string, CrosslinkMetadata>();

    // First, scan for already-properly-formatted fire codes that won't appear in changes
    this.detectExistingFireCodes(logBody, metadata, processedItems);

    // Process each change to build metadata, avoiding duplicates
    for (const change of standardResult.changes) {
      if (change.original && change.enhanced) {
        const key = `${change.original}:${change.type}`;

        // Skip if we've already processed this exact item and type
        if (processedItems.has(key)) {
          continue;
        }

        const crosslinkInfo = this.determineCrosslinkType(change.original, change.enhanced, change.type);

        if (crosslinkInfo) {
          // This is a crosslinkable enhancement
          const crosslinkData: CrosslinkMetadata = {
            original: change.original,
            enhanced: change.enhanced,
            position: logBody.indexOf(change.original),
            crosslink: true,
            searchTerm: crosslinkInfo.searchTerm,
            type: crosslinkInfo.type
          };

          // Add to appropriate metadata category (avoid duplicates)
          const metadataKey = `${crosslinkInfo.type}:${change.original}`;
          if (!metadataMap.has(metadataKey)) {
            metadataMap.set(metadataKey, crosslinkData);

            if (crosslinkInfo.type === 'fire_code') {
              metadata.fire_codes.push(crosslinkData);
            } else if (crosslinkInfo.type === 'crew_id') {
              metadata.crews.push(crosslinkData);
            } else if (crosslinkInfo.type === 'aircraft_id') {
              metadata.aircraft.push(crosslinkData);
            }
          }
        } else {
          // Non-crosslinkable general enhancement
          const generalKey = `general:${change.original}`;
          if (!metadataMap.has(generalKey)) {
            const generalData: CrosslinkMetadata = {
              original: change.original,
              enhanced: change.enhanced,
              position: logBody.indexOf(change.original),
              crosslink: false
            };
            metadataMap.set(generalKey, generalData);
            metadata.general.push(generalData);
          }
        }

        processedItems.add(key);
      }
    }

    return {
      body: logBody,
      enhanced_body: enhanced_body,
      enhancement_metadata: metadata,
      // Legacy compatibility
      enhanced_text: standardResult.enhanced_text,
      changes: standardResult.changes,
      tags: standardResult.tags,
      tag_rationales: standardResult.tag_rationales,
      original_text: standardResult.original_text
    };
  }

  /**
   * Scan text for already-properly-formatted fire codes that won't appear in changes
   */
  private detectExistingFireCodes(text: string, metadata: EnhancementMetadata, processedItems: Set<string>): void {
    // Fire code pattern: 2-3 letters + 3 digits + optional dash + 2 digits
    const fireCodePattern = /\b([A-Z]{2,3}[0-9]{3}(?:-[0-9]{2})?)\b/g;

    let match;
    while ((match = fireCodePattern.exec(text)) !== null) {
      const fireCode = match[1];
      const position = match.index;
      const key = `${fireCode}:existing_fire_code`;

      // Check if this fire code is already processed
      if (processedItems.has(key)) {
        continue;
      }

      // Check if this fire code is already in metadata (from changes)
      const alreadyExists = metadata.fire_codes.some(existing =>
        existing.original === fireCode || existing.enhanced === fireCode
      );

      if (!alreadyExists) {
        // Add as crosslinkable fire code
        metadata.fire_codes.push({
          original: fireCode,
          enhanced: fireCode,
          position: position,
          crosslink: true,
          searchTerm: fireCode,
          type: 'fire_code'
        });

        processedItems.add(key);
      }
    }
  }

  /**
   * Determine if an enhancement should be crosslinked and what type
   */
  private determineCrosslinkType(original: string, enhanced: string, changeType: string): { type: 'fire_code' | 'crew_id' | 'aircraft_id', searchTerm: string } | null {
    // STRICT FILTERING: Only specific types should be crosslinked

    // EXCLUDE common non-crosslinkable enhancements
    if (this.isNonCrosslinkableEnhancement(original, enhanced, changeType)) {
      return null;
    }

    // Fire code detection - ONLY actual fire codes
    if (changeType === 'fire_standardization' ||
      changeType === 'fire_code_standardization' ||
      this.isFireCode(original, enhanced)) {
      return {
        type: 'fire_code',
        searchTerm: enhanced // Search using standardized version
      };
    }

    // Crew identification - ONLY crew identifiers (SS9, BD15, etc.)
    if (this.isCrewIdentifier(original, enhanced)) {
      return {
        type: 'crew_id',
        searchTerm: original // Search using original abbreviation
      };
    }

    // Aircraft identification - ONLY specific aircraft callsigns
    if (this.isSpecificAircraft(original, enhanced)) {
      return {
        type: 'aircraft_id',
        searchTerm: original // Search using original callsign
      };
    }

    // EVERYTHING ELSE IS NOT CROSSLINKABLE
    // This includes: geographic names, terminology, abbreviations, times, etc.
    return null;
  }

  /**
   * Check if enhancement should be excluded from crosslinking
   */
  private isNonCrosslinkableEnhancement(original: string, enhanced: string, changeType: string): boolean {
    // Geographic names (Smith -> Fort Smith, etc.)
    const geographicTerms = ['smith', 'hay river', 'enterprise', 'liard', 'yellowknife'];

    // Technical terminology expansions
    const terminologyTerms = ['cold trail', 'trail', 'scan', 'hot spots', 'suppressing'];

    // Time standardizations
    const timePattern = /^\d{1,2}:?\d{0,2}(hrs?|am|pm)?$/i;

    // Abbreviation expansions (but not crew/fire codes)
    const commonAbbreviations = ['temp', 'rdo', 'smado', 'jp', 'call', 'base'];

    const originalLower = original.toLowerCase();
    const enhancedLower = enhanced.toLowerCase();

    // Exclude geographic expansions
    if (geographicTerms.some(term => originalLower === term || enhancedLower.includes(term))) {
      return true;
    }

    // Exclude terminology expansions  
    if (terminologyTerms.some(term => originalLower.includes(term) || enhancedLower.includes(term))) {
      return true;
    }

    // Exclude time standardizations
    if (timePattern.test(original)) {
      return true;
    }

    // Exclude common abbreviations (that aren't crew/fire codes)
    if (commonAbbreviations.some(term => originalLower === term)) {
      return true;
    }

    return false;
  }

  /**
   * Check if enhancement is a fire code standardization
   */
  private isFireCode(original: string, enhanced: string): boolean {
    // Fire codes follow patterns like SS009, SS009-24, SS025-25
    const fireCodePattern = /^[A-Z]{2}[0-9]{3}(-[0-9]{2})?$/;

    // Check if either original or enhanced matches fire code pattern
    return fireCodePattern.test(original) || fireCodePattern.test(enhanced);
  }

  /**
   * Check if enhancement is a crew identifier - STRICT VALIDATION
   */
  private isCrewIdentifier(original: string, enhanced: string): boolean {
    // ONLY crew codes like SS9, BD15, etc. - NOT geographic names or terminology
    const strictCrewPattern = /^[A-Z]{2}[0-9]+$/;  // Exact format: 2 letters + numbers

    // Must match crew pattern AND enhanced version must contain "crew"
    return strictCrewPattern.test(original.trim()) &&
      enhanced.toLowerCase().includes('crew');
  }

  /**
   * Check if enhancement is a specific aircraft identifier - STRICT VALIDATION
   */
  private isSpecificAircraft(original: string, enhanced: string): boolean {
    // ONLY specific aircraft callsigns with numbers - NOT groups or terminology
    const aircraftCallsignPattern = /^[A-Z]{2,3}[0-9]+$/; // BD101, etc.
    const birdDogPattern = /^Bird\s+Dog\s+[0-9]+$/i; // "Bird Dog 101"

    // Must be specific aircraft callsign format
    return aircraftCallsignPattern.test(original.trim()) ||
      birdDogPattern.test(original.trim()) ||
      (original.includes('101') && enhanced.toLowerCase().includes('bird dog'));
  }
}

// Export for use in components
export default DutyLogEnhancer;
